import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { QuickSearchLocationItem } from '@fc-search/models/quick-search';
import { QuickSearchFacadeService } from '@fc-search/services/quick-search-facade.service';
import { ButtonComponent } from '@fc-shared/ui/buttons/button.component';
import { TooltipDirective } from '@fc-shared/ui/tooltips/tooltip.directive';

@Component({
  selector: 'fc-qs-location-result',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule, IconComponent, ButtonComponent, TooltipDirective],
  template: `
    <div
      class="flex gap-12 items-center max-w-full relative py-8 pl-14 pr-16 hover:bg-secondary-50 cursor-pointer group border-l-2"
      [class.border-white]="!selected()"
      [class.border-primary-600]="selected()"
      [class.bg-secondary-50]="selected()"
      [routerLink]="['/locations/main/location-list', result().obj.id]"
    >
      <div
        class="size-40 shrink-0 rounded-xl bg-secondary-50 flex items-center justify-center"
      >
        <div
          class="flex items-center justify-center size-34 rounded-br-full rounded-t-full rotate-[-45deg]"
          [style.background]="result().obj.type?.color"
        >
          <div
            class="flex items-center justify-center size-24 rounded-full bg-white"
          >
            <fc-icon
              class="rounded-full rotate-45 border-none"
              [style.background-color]="result().obj.type?.color"
              [icon]="$safeNavigationMigration(result().obj.type?.icon)"
              [size]="16"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-4 grow min-w-0">
        <span class="text-sm font-medium text-grey-900 truncate">
          {{ result().obj.name }}
        </span>
        <div class="flex gap-4 items-center truncate">
          <div
            class="flex items-center justify-center size-16 rounded-full"
            [style.background]="result().obj.type?.color"
          >
            <fc-icon
              [icon]="$safeNavigationMigration(result().obj.type?.icon)"
              color="white"
              [size]="10"
            />
          </div>
          <span class="text-xs text-secondary-500">
            {{ result().obj.type?.name }}
          </span>
        </div>
      </div>
      <div
        class="flex gap-4 p-4 rounded-xl bg-white border-secondary-100 shadow-sm"
        [class.opacity-0]="!selected()"
        [class.opacity-100]="selected()"
        [class.group-hover:opacity-100]="!selected()"
      >
        <div
          fcTooltip="Open in new tab"
          [showTooltip]="selected() && activeButtonIndex() === 0"
        >
          <fc-button
            variant="filled"
            color="primary"
            [focused]="activeButtonIndex() === 0"
            icon="icon-new-window"
            size="sm"
            iconPosition="only"
            (click)="viewDetails(); $event.stopPropagation()"
          />
        </div>
      </div>
    </div>
  `,
})
export class QsLocationResultComponent {
  result = input.required<QuickSearchLocationItem>();
  selected = input(false);
  activeButtonIndex = input(-1);
  quickAction = output();
  private router = inject(Router);
  private facade = inject(QuickSearchFacadeService);

  activateButton(index: number): void {
    if (index === 0) {
      this.viewDetails();
    }
  }

  viewDetails(): void {
    this.quickAction.emit();
    const url = this.router.serializeUrl(
      this.router.createUrlTree([
        '/locations/main/location-list/' + this.result().obj.id,
      ]),
    );
    window.open(url, '_blank');
    this.facade.clearAndClose();
  }
}
